import React from "react";

const CabinetRow = ({
  cab,
  index,
  country,
  shakerData,
  cabinetTypes,
  colorCodeData,
  fixedPriceItems = {},
  pvcRollingShutterSizes = {},
  profileHandlePrices = {},
  handleCabinetChange,
  handleKeyDown,
  handleDeleteCabinet,
  calculateTotal,
  calculateSqft,
  isCabinetValid,
}) => {
  const inputClasses = "w-full px-3 py-2 bg-white border border-indigo-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-gray-700 text-sm";
  const selectClasses = "w-full px-3 py-2 bg-white border border-indigo-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-gray-700 text-sm";

  const getColorPriceInfo = (colorCode) => {
    if (!colorCodeData || !colorCodeData[colorCode]) return "";
    
    const colorInfo = colorCodeData[colorCode];
    
    if (typeof colorInfo === 'object') {
      const parts = [];
      if (colorInfo.material) parts.push(`Material: ${colorInfo.material}`);
      if (colorInfo.unitCost) parts.push(`Cost: $${colorInfo.unitCost}`);
      if (colorInfo.shutterPrice) parts.push(`Shutter: $${colorInfo.shutterPrice}`);
      
      return parts.join(' | ');
    }
    
    return String(colorInfo);
  };

  // Check if this cabinet has all required fields for calculation
  const canCalculateTotal = () => {
    if (country === "Shaker") {
      return cab.type && cab.unitCost && cab.qty;
    } else {
      const isFixedPriceItem = fixedPriceItems && fixedPriceItems[cab.type];
      const isPVCRollingShutter = cab.type === "PVC Rolling Shutter";
      const isProfileHandle = cab.type === "Profile Handle";
      
      if (isPVCRollingShutter) {
        return cab.type && cab.pvcSize && cab.color && cab.qty;
      }
      if (isProfileHandle) {
        return cab.type && cab.profileHandleInches && cab.qty;
      }
      if (isFixedPriceItem) {
        return cab.type && cab.qty;
      }
      return cab.type && cab.height && cab.width && cab.color && cab.unitCost;
    }
  };

  // Check cabinet types
  const isFixedPriceItem = fixedPriceItems && fixedPriceItems[cab.type];
  const isPVCRollingShutter = cab.type === "PVC Rolling Shutter";
  const isProfileHandle = cab.type === "Profile Handle";

  return (
    <tr 
      className={`hover:bg-indigo-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-indigo-25'}`}
      data-row-index={index}
    >
      {/* Type Column - SHOW SHAKER FIRST */}
      <td className="p-3">
        {country === "Shaker" ? (
          <select
            value={cab.type || ""}
            onChange={(e) => handleCabinetChange(index, "type", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={selectClasses}
          >
            <option value="" className="text-gray-400">Select Shaker Item</option>
            {shakerData && shakerData.map((item) => (
              <option key={item.item} value={item.item} className="text-gray-700">
                {item.item} - ${item.unitCost}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={cab.type || ""}
            onChange={(e) => handleCabinetChange(index, "type", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={selectClasses}
          >
            <option value="" className="text-gray-400">Select Cabinet Type</option>
            {/* Show Shaker as first option if available */}
            {shakerData && shakerData.length > 0 && (
              <optgroup label="Shaker Cabinets">
                {shakerData.map((item) => (
                  <option key={`shaker-${item.item}`} value={item.item} className="text-gray-700">
                    {item.item} - ${item.unitCost}
                  </option>
                ))}
              </optgroup>
            )}
            {/* Show other cabinet types */}
            {cabinetTypes && cabinetTypes.map((type) => (
              <option key={type} value={type} className="text-gray-700">
                {type}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Dynamic Columns based on country */}
      {country === "Shaker" ? (
        <>
          {/* SHAKER CABINET FIELDS */}
          <td className="p-3">
            <input
              type="text"
              value={cab.description || ""}
              readOnly
              className={`${inputClasses} bg-indigo-50 text-gray-600`}
              placeholder="Auto-filled description"
            />
          </td>
          <td className="p-3">
            <input
              type="number"
              value={cab.unitCost || ""}
              readOnly
              className={`${inputClasses} bg-indigo-50 text-gray-600 font-medium`}
              placeholder="Unit cost"
            />
          </td>
          <td className="p-3">
            <input
              type="number"
              value={cab.qty || ""}
              onChange={(e) => handleCabinetChange(index, "qty", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="Quantity"
              min="1"
            />
          </td>
          <td className="p-3">
            <input
              type="number"
              value={cab.discount || ""}
              onChange={(e) => handleCabinetChange(index, "discount", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="Discount %"
              min="0"
              max="100"
            />
          </td>
          <td className="p-3 font-semibold text-gray-800 text-lg">
            {canCalculateTotal() ? `$${calculateTotal(cab).toFixed(2)}` : "$0.00"}
          </td>
        </>
      ) : (
        <>
          {/* REGULAR CABINET FIELDS */}
          
          {/* Special Size/Inches Selection */}
          {isProfileHandle ? (
            <td className="p-3" colSpan="2">
              <select
                value={cab.profileHandleInches || ""}
                onChange={(e) => handleCabinetChange(index, "profileHandleInches", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={selectClasses}
              >
                <option value="" className="text-gray-400">Select Inches</option>
                {profileHandlePrices && Object.keys(profileHandlePrices).map((inches) => (
                  <option key={inches} value={inches} className="text-gray-700">
                    {inches}
                  </option>
                ))}
              </select>
            </td>
          ) : isPVCRollingShutter ? (
            <td className="p-3" colSpan="2">
              <select
                value={cab.pvcSize || ""}
                onChange={(e) => handleCabinetChange(index, "pvcSize", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={selectClasses}
              >
                <option value="" className="text-gray-400">Select Size</option>
                {pvcRollingShutterSizes && Object.keys(pvcRollingShutterSizes).map((size) => (
                  <option key={size} value={size} className="text-gray-700">
                    {size} - ${pvcRollingShutterSizes[size]}
                  </option>
                ))}
              </select>
            </td>
          ) : (
            <>
              {/* Width and Height for regular cabinets */}
              <td className="p-3">
                <input
                  type="number"
                  value={cab.width || ""}
                  onChange={(e) => handleCabinetChange(index, "width", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={inputClasses}
                  placeholder="Width (in)"
                  disabled={isFixedPriceItem}
                />
              </td>
              <td className="p-3">
                <input
                  type="number"
                  value={cab.height || ""}
                  onChange={(e) => handleCabinetChange(index, "height", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={inputClasses}
                  placeholder="Height (in)"
                  disabled={isFixedPriceItem}
                />
              </td>
            </>
          )}

          {/* Color Selection */}
          <td className="p-3">
            <select
              value={cab.color || ""}
              onChange={(e) => handleCabinetChange(index, "color", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={selectClasses}
              disabled={(isFixedPriceItem && !isPVCRollingShutter) || isProfileHandle}
            >
              <option value="" className="text-gray-400">Select Color</option>
              {isPVCRollingShutter ? (
                // PVC Rolling Shutter specific colors
                <>
                  <option value="Aluminium" className="text-gray-700">Aluminium</option>
                  <option value="Black" className="text-gray-700">Black</option>
                  <option value="White" className="text-gray-700">White</option>
                </>
              ) : (
                // Regular colors for other cabinets
                colorCodeData && Object.keys(colorCodeData).map((colorCode) => (
                  <option key={colorCode} value={colorCode} className="text-gray-700">
                    {colorCode} 
                  </option>
                ))
              )}
            </select>
          </td>

          {/* Material (Read-only) */}
          <td className="p-3">
            <input
              type="text"
              value={cab.material || ""}
              readOnly
              className={`${inputClasses} bg-indigo-50 text-gray-600`}
              placeholder="Material"
            />
          </td>

          {/* Square Footage */}
          <td className="p-3">
            <div className="text-center">
              <div className="text-sm font-semibold text-gray-700">
                {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? "N/A" : `${calculateSqft(cab).toFixed(2)} sq.ft.`}
              </div>
            </div>
          </td>

          {/* Drawer Slides & Hinges - ONLY FOR CUSTOM CABINETS */}
          <td className="p-3">
            <input
              type="number"
              value={cab.drawerSlides || ""}
              onChange={(e) => handleCabinetChange(index, "drawerSlides", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="Qty"
              min="0"
              disabled={isFixedPriceItem || isPVCRollingShutter || isProfileHandle}
            />
          </td>

          <td className="p-3">
            <input
              type="number"
              value={cab.hinges || ""}
              onChange={(e) => handleCabinetChange(index, "hinges", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="Qty"
              min="0"
              disabled={isFixedPriceItem || isPVCRollingShutter || isProfileHandle}
            />
          </td>

          {/* Discount */}
          <td className="p-3">
            <input
              type="number"
              value={cab.discount || ""}
              onChange={(e) => handleCabinetChange(index, "discount", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="%"
              min="0"
              max="100"
            />
          </td>

          {/* Quantity */}
          <td className="p-3">
            <input
              type="number"
              value={cab.qty || ""}
              onChange={(e) => handleCabinetChange(index, "qty", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={inputClasses}
              placeholder="Qty"
              min="1"
            />
          </td>

          {/* Total Price */}
          <td className="p-3 font-semibold text-gray-800 text-lg">
            {canCalculateTotal() ? `$${calculateTotal(cab).toFixed(2)}` : "$0.00"}
          </td>
        </>
      )}

      {/* Action Column - Common for both */}
      <td className="p-3">
        {index > 0 && (
          <button
            onClick={() => handleDeleteCabinet(index)}
            className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 w-10 h-10 flex items-center justify-center font-bold"
            title="Delete Row"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  );
};

export default CabinetRow;